import { PropsWithChildren, useCallback, useState } from "react"
import { Lock, Plus } from "@phosphor-icons/react"
import {
  Select,
  SelectItem,
  SelectContent,
  SelectTrigger,
  SelectValue,
  Button,
  Input,
  Checkbox,
} from "@repo/ui"
import { useTranslation } from "react-i18next"
import { useDebounceCallback } from "usehooks-ts"
import { Trash2 } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card.tsx"
import {
  useCreateOpexCategory,
  useDeleteOpexCategory,
  useUpdateOpexSelectedCategory,
} from "@/feature/financial-model/ecommerce/opex/api"
import {
  CardBox,
  CardLayoutBox,
} from "@/components/shared/cards/SelectCards.tsx"
import {
  OpexCategory,
  OpexModelingApproach,
} from "@/feature/financial-model/ecommerce/opex/api/types.ts"
import {
  OPEX_GRANULARITY_LEVELS,
  OPEX_MODELLING_APPROACH,
} from "@/feature/financial-model/ecommerce/opex/constants.ts"
import {
  ActionableCheckbox,
  ActionableTableRow,
  TableActionPopover,
  TableActionPopoverProvider,
  useTableActionPopoverStore,
} from "@/components/shared/tables/popovers.tsx"
import {
  RowHeader,
  Table,
  TableBody,
  TableCell,
  TableCellContent,
  TableFooter,
  TableHead,
  TableHeader,
  TableHeaderIcons,
  TableRow,
} from "@/components/ui/table.tsx"
import { OptionalTooltip, TooltipProvider } from "@/components/ui/tooltip.tsx"
import {
  TableErrorProvider,
  useTableErrorContext,
} from "../../../revenues/context/TableErrorContext"
import { cn } from "@/lib/utils"
import { TableRowButton } from "@/components/shared/tables"
// import { OpexModellingPreviewDialogTrigger } from "@/feature/financial-model/ecommerce/opex/components/setup/modelling-preview-table.tsx"
import { useOpexSelectedCategory } from "@/feature/financial-model/ecommerce/opex/hooks/use-opex-selected-category.tsx"

type SelectedCategoryProps = {
  financialModelId: string
  selectedCategoryId: string
}

type SubCategoryProps = SelectedCategoryProps & {
  subCategoryId: string
}

const AddSubCostCategoryButton = (
  selectedCategoryProps: SelectedCategoryProps
) => {
  const { t } = useTranslation(`opex`)
  const { onAddSubCostCategory } = useOpexSelectedCategory(
    selectedCategoryProps
  )
  return (
    <Button
      variant={`secondary`}
      onClick={() => onAddSubCostCategory()}
      className="rounded-md bg-white"
    >
      <Plus size={16} className="mr-2" />
      {t(`opex.setup.category_accordion_item.detailed.add_sub_category`)}
    </Button>
  )
}

const TopLevelSelectedCategorySettings = (
  selectedCategoryProps: SelectedCategoryProps
) => {
  const { t } = useTranslation(`opex`)
  const {
    data: selectedCategory,
    onGranularityChange,
    onModelingApproachChange,
  } = useOpexSelectedCategory(selectedCategoryProps)
  const { granularity_level, modeling_approach } = selectedCategory

  // with channels means category was revenues modeled before
  // so we cannot switch to aggregate granularity
  const isGranularityForceDetailed =
    selectedCategory.opex_category.channels.length > 0

  const granularityLevel = isGranularityForceDetailed
    ? OPEX_GRANULARITY_LEVELS.DETAILED_EXPENSES
    : granularity_level

  return (
    <CardLayoutBox className="items-end">
      <CardBox variant="left">
        <div className="flex items-center gap-2">
          <h3>{t(`opex.setup.category_accordion_item.granularity.label`)}</h3>
          <div className="h-8 w-4" />
        </div>
        <div className="mt-2">
          <Select
            disabled={isGranularityForceDetailed}
            onValueChange={onGranularityChange}
            value={granularityLevel}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Granularity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={OPEX_GRANULARITY_LEVELS.AGGREGATE_EXPENSES}>
                {t(`opex.setup.category_accordion_item.granularity.aggregate`)}
              </SelectItem>
              <SelectItem value={OPEX_GRANULARITY_LEVELS.DETAILED_EXPENSES}>
                {t(`opex.setup.category_accordion_item.granularity.detailed`)}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardBox>
      <CardBox variant="right">
        <div className="flex items-center gap-2">
          <h3>
            {t(`opex.setup.category_accordion_item.modelling_approach.label`)}
          </h3>
          {/* {granularityLevel !== OPEX_GRANULARITY_LEVELS.DETAILED_EXPENSES &&
            selectedCategory.opex_category.children.length === 0 && (
              <OpexModellingPreviewDialogTrigger />
            )} */}
        </div>
        <OptionalTooltip
          content={t(`opex.setup.tooltip.locked_categories`)}
          show={
            granularityLevel === OPEX_GRANULARITY_LEVELS.DETAILED_EXPENSES ||
            selectedCategory.opex_category.children.length > 0
          }
        >
          <div className="mt-2">
            <Select
              onValueChange={onModelingApproachChange}
              value={
                granularityLevel ===
                  OPEX_GRANULARITY_LEVELS.DETAILED_EXPENSES ||
                selectedCategory.opex_category.children.length > 0
                  ? ``
                  : modeling_approach
              }
              disabled={
                granularityLevel ===
                  OPEX_GRANULARITY_LEVELS.DETAILED_EXPENSES ||
                selectedCategory.opex_category.children.length > 0
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Granularity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={OPEX_MODELLING_APPROACH.ABSOLUTE_VALUE}>
                  {t(
                    `opex.setup.category_accordion_item.modelling_approach.absolute`
                  )}
                </SelectItem>
                <SelectItem
                  value={OPEX_MODELLING_APPROACH.PERCENTAGE_OF_REVENUE}
                >
                  {t(
                    `opex.setup.category_accordion_item.modelling_approach.percent`
                  )}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </OptionalTooltip>
      </CardBox>
    </CardLayoutBox>
  )
}

const useChildCategory = ({
  financialModelId,
  parentCategory,
  childCategoryId,
}: {
  financialModelId: string
  parentCategory: OpexCategory
  childCategoryId: string
}) => {
  const childCategory = parentCategory.children.find(
    (c) => c.id === childCategoryId
  )
  if (!childCategory) {
    throw new Error(
      `useChildCategory > Selected child category ${childCategoryId} not found`
    )
  }
  const { mutate: createOpexCategory } = useCreateOpexCategory()
  const { mutate: updateCategory, isPending } = useUpdateOpexSelectedCategory()
  const { mutate: deleteCategory, isPending: isDeleting } =
    useDeleteOpexCategory()

  const [name, setName] = useState(childCategory.name)
  const onNameChanged = useCallback(
    (value: string) => {
      updateCategory({
        financialModelId,
        opex_category_id: childCategory.id,
        is_selected: true,
        name: value,
        modeling_approach: childCategory.modeling_approach!,
      })
    },
    [
      childCategory.id,
      childCategory.modeling_approach,
      financialModelId,
      updateCategory,
    ]
  )
  const debounceOnNameChanged = useDebounceCallback(onNameChanged, 300)

  const onNameChange = useCallback(
    (value: string) => {
      setName(value)
      debounceOnNameChanged(value)
    },
    [debounceOnNameChanged]
  )

  const onModellingApproachChange = (value: OpexModelingApproach) => {
    updateCategory({
      financialModelId,
      opex_category_id: childCategory.id,
      is_selected: true,
      name: childCategory.name,
      modeling_approach: value,
    })
  }

  const onAddChild = (addName: string = ``) => {
    createOpexCategory({
      financialModelId,
      parent_id: childCategory.id,
      name: addName,
      modeling_approach: OPEX_MODELLING_APPROACH.ABSOLUTE_VALUE,
      granularity_level: OPEX_GRANULARITY_LEVELS.AGGREGATE_EXPENSES,
    })
  }

  const onDelete = useCallback(() => {
    deleteCategory({
      financialModelId,
      categoryId: childCategory.id,
    })
  }, [deleteCategory, financialModelId, childCategory.id])

  const onDeleteChild = useCallback(
    (id: string) => {
      if (!childCategory.children.find((c) => c.id === id)) {
        throw new Error(
          `useChildCategory > Selected child category ${id} not found`
        )
      }

      deleteCategory({
        financialModelId,
        categoryId: id,
      })
    },
    [childCategory.children, deleteCategory, financialModelId]
  )

  return {
    data: childCategory,
    onModellingApproachChange,
    name,
    onNameChange,
    onAddChild,
    onDelete,
    onDeleteChild,
    hasChildren: childCategory.children.length > 0,
    isUpdating: isPending,
    isDeleting,
  }
}

const useSelectedCategorySubCategory = ({
  financialModelId,
  selectedCategoryId,
  subCategoryId,
}: SubCategoryProps) => {
  const { data: parentCategory } = useOpexSelectedCategory({
    financialModelId,
    selectedCategoryId,
  })

  const {
    data: subCategory,
    onModellingApproachChange,
    name,
    onNameChange,
    onAddChild,
    onDelete,
    isDeleting,
    hasChildren,
    onDeleteChild,
    isUpdating,
  } = useChildCategory({
    financialModelId,
    parentCategory: parentCategory.opex_category,
    childCategoryId: subCategoryId,
  })
  return {
    data: subCategory,
    onModellingApproachChange,
    name,
    onNameChange,
    onAddDetailedSubCostCategory: onAddChild,
    onDelete,
    onDeleteChild,
    isDeleting,
    hasDetailedSubCosts: hasChildren,
    isUpdating,
  }
}

const AddDetailedSubCostCategoryButton = (
  subCategoryProps: SubCategoryProps
) => {
  const { t } = useTranslation(`opex`)
  const { onAddDetailedSubCostCategory } =
    useSelectedCategorySubCategory(subCategoryProps)
  return (
    <Button
      variant={`secondary`}
      onClick={() => {
        onAddDetailedSubCostCategory()
      }}
      className="rounded-md w-full"
    >
      <Plus size={16} className="mr-2" />
      {t(`opex.setup.category_accordion_item.detailed.add_detailed_sub_cost`)}
    </Button>
  )
}

const SecondLevelSubCategory = ({
  children,
  ...subCategoryProps
}: PropsWithChildren<SubCategoryProps>) => {
  const { t } = useTranslation(`opex`)

  const {
    data: subCategory,
    onModellingApproachChange,
    onDelete,
    isDeleting,
    name,
    onNameChange,
    isUpdating,
  } = useSelectedCategorySubCategory(subCategoryProps)

  return (
    <Card className={`pt-6 relative`} disabled={isDeleting}>
      <div className="absolute top-4 right-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onDelete}
          disabled={isDeleting}
        >
          <Trash2 size={16} className="text-red-500 text-secondary" />
        </Button>
      </div>
      <CardContent className={`flex flex-col gap-4`}>
        <CardLayoutBox className="items-end">
          <CardBox variant="left">
            <div className="flex items-center gap-2">
              <h3>
                {t(
                  `opex.setup.category_accordion_item.detailed.second_level_category`
                )}
              </h3>
            </div>
            <div className="mt-2">
              <Input
                autoFocus
                type="text"
                className={`mt-0 h-10`}
                value={name}
                onChange={(e) => {
                  onNameChange(e.target.value)
                }}
              />
            </div>
          </CardBox>
          <CardBox variant="right">
            <div className="flex items-center gap-2">
              <h3>
                {t(
                  `opex.setup.category_accordion_item.modelling_approach.label`
                )}
              </h3>
              {/* {subCategory.children.length === 0 && (
                <OpexModellingPreviewDialogTrigger />
              )} */}
            </div>
            <div className="mt-2">
              <Select
                onValueChange={onModellingApproachChange}
                value={
                  subCategory.children.length > 0
                    ? ``
                    : subCategory.modeling_approach!
                }
                disabled={
                  !name || isUpdating || subCategory.children.length > 0
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Granularity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OPEX_MODELLING_APPROACH.ABSOLUTE_VALUE}>
                    {t(
                      `opex.setup.category_accordion_item.modelling_approach.absolute`
                    )}
                  </SelectItem>
                  <SelectItem
                    value={OPEX_MODELLING_APPROACH.PERCENTAGE_OF_REVENUE}
                  >
                    {t(
                      `opex.setup.category_accordion_item.modelling_approach.percent`
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardBox>
        </CardLayoutBox>
        {children}
      </CardContent>
    </Card>
  )
}

type DetailedSubCostCategoryProps = SubCategoryProps & {
  detailedSubCostCategoryId: string
}

const useSelectedCategoryDetailedSubCategoryTableRow = ({
  financialModelId,
  selectedCategoryId,
  subCategoryId,
  detailedSubCostCategoryId,
}: DetailedSubCostCategoryProps) => {
  const { t: commonT } = useTranslation(`common`)
  const { addError } = useTableErrorContext()
  const { data: topLevelParentCategory } = useOpexSelectedCategory({
    financialModelId,
    selectedCategoryId,
  })

  const subCategory = topLevelParentCategory.opex_category.children.find(
    (c) => c.id === subCategoryId
  )
  if (!subCategory) {
    throw new Error(
      `useSelectedCategoryDetailedSubCategory > Selected sub category ${subCategoryId} not found`
    )
  }

  const { data, onModellingApproachChange, onNameChange, isUpdating } =
    useChildCategory({
      financialModelId,
      parentCategory: subCategory,
      childCategoryId: detailedSubCostCategoryId,
    })

  return {
    topLevelParentCategory,
    parentSubCategory: subCategory,
    data,
    onModellingApproachChange,
    onNameChanged: useCallback(
      (value: string) => {
        if (
          subCategory.children.find(
            (c) => c.id !== detailedSubCostCategoryId && c.name === value
          )
        ) {
          addError(commonT(`table.error.name_not_unique`), data.id)
          return
        }
        onNameChange(value)
      },
      [
        addError,
        commonT,
        data.id,
        detailedSubCostCategoryId,
        onNameChange,
        subCategory.children,
      ]
    ),
    isLoading: isUpdating,
  }
}

const DetailedSubCostCategoryTableRow = (
  detailedProps: DetailedSubCostCategoryProps
) => {
  const { t } = useTranslation(`opex`)
  const {
    data: detailedSubCostCategory,
    onModellingApproachChange,
    onNameChanged,
  } = useSelectedCategoryDetailedSubCategoryTableRow(detailedProps)
  const { removeError, errors } = useTableErrorContext()
  const [inputName, setInputName] = useState(detailedSubCostCategory.name)
  const onFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      removeError(e.target.id)
    },
    [removeError]
  )

  const handleBlur = useCallback(() => {
    onNameChanged(inputName)
  }, [inputName, onNameChanged])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === `Enter`) {
        e.preventDefault()
        e.currentTarget.blur()
      }
    },
    []
  )
  return (
    <ActionableTableRow
      key={detailedSubCostCategory.id}
      id={detailedSubCostCategory.id}
    >
      <TableCell role="checkbox">
        <ActionableCheckbox />
      </TableCell>
      <TableCell className="w-[calc(50%-3rem)]">
        <TableCellContent type="input" className="!justify-start max-w-none">
          <Input
            className={cn(
              `table-input pl-0 bg-transparent`,
              errors.some((error) => error.id === detailedSubCostCategory.id) &&
                `border-red-400 text-red-400`
            )}
            autoFocus
            id={detailedSubCostCategory.id}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            value={inputName}
            onChange={(e) => {
              setInputName(e.target.value)
            }}
            onFocus={onFocus}
          />
        </TableCellContent>
      </TableCell>
      <TableCell className="w-1/2">
        <Select
          onValueChange={onModellingApproachChange}
          defaultValue={detailedSubCostCategory.modeling_approach!}
        >
          <SelectTrigger className="border-none focus-within:ring-transparent">
            <SelectValue placeholder="Select Granularity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={OPEX_MODELLING_APPROACH.ABSOLUTE_VALUE}>
              {t(
                `opex.setup.category_accordion_item.modelling_approach.absolute`
              )}
            </SelectItem>
            <SelectItem value={OPEX_MODELLING_APPROACH.PERCENTAGE_OF_REVENUE}>
              {t(
                `opex.setup.category_accordion_item.modelling_approach.percent`
              )}
            </SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
    </ActionableTableRow>
  )
}

const DetailedSubCostCategoriesTable = ({
  financialModelId,
  selectedCategoryId,
  subCategoryId,
}: SubCategoryProps) => {
  const { t } = useTranslation(`opex`)

  const {
    data: subCategory,
    onDeleteChild,
    onAddDetailedSubCostCategory,
  } = useSelectedCategorySubCategory({
    financialModelId,
    selectedCategoryId,
    subCategoryId,
  })

  const detailedSubCostCategories = subCategory.children

  const isAllSelected = useTableActionPopoverStore((state) => state.allSelected)
  const setCheckedDetailedSubCosts = useTableActionPopoverStore(
    (state) => state.setSelected
  )
  const selectedDetailedSubCosts = useTableActionPopoverStore(
    (state) => state.selected
  )

  const handleSelectAll = useCallback(() => {
    setCheckedDetailedSubCosts(
      isAllSelected ? [] : detailedSubCostCategories.map((c) => c.id)
    )
  }, [isAllSelected, detailedSubCostCategories, setCheckedDetailedSubCosts])

  const onDeleteSelected = useCallback(() => {
    selectedDetailedSubCosts.forEach(onDeleteChild)
  }, [onDeleteChild, selectedDetailedSubCosts])

  return (
    <TooltipProvider>
      <TableErrorProvider>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="flex items-center max-w-6">
                <TableActionPopover
                  onDelete={onDeleteSelected}
                  maxItems={detailedSubCostCategories.length}
                >
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                  />
                </TableActionPopover>
              </TableHead>
              <TableHead>
                {t(
                  `opex.setup.category_accordion_item.detailed.table_head.third_level_category`
                )}
              </TableHead>
              <TableHead>
                <span className="inline-flex items-center gap-2">
                  <TableHeaderIcons icon="options" />
                  {t(
                    `opex.setup.category_accordion_item.detailed.table_head.modelling_approach`
                  )}
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {detailedSubCostCategories.map((detailedSubCostCategory) => (
              <DetailedSubCostCategoryTableRow
                key={detailedSubCostCategory.id}
                financialModelId={financialModelId}
                selectedCategoryId={selectedCategoryId}
                subCategoryId={subCategoryId}
                detailedSubCostCategoryId={detailedSubCostCategory.id}
              />
            ))}
          </TableBody>
          <TableFooter className="bg-transparent p-0">
            <TableRowButton
              onClick={() => onAddDetailedSubCostCategory()}
              span={3}
            />
          </TableFooter>
        </Table>
      </TableErrorProvider>
    </TooltipProvider>
  )
}

const SelectedCategoryChannel = ({
  financialModelId,
  selectedCategoryId,
  channelId,
  children,
}: PropsWithChildren<{
  financialModelId: string
  selectedCategoryId: string
  channelId: string
}>) => {
  const { t } = useTranslation(`opex`)
  const { data: selectedCategory } = useOpexSelectedCategory({
    financialModelId,
    selectedCategoryId,
  })
  const { channels } = selectedCategory.opex_category

  const channel = channels.find((c) => c.id === channelId)

  if (!channel) {
    throw new Error(`SelectedCategoryChannel > Channel ${channelId} not found`)
  }

  return (
    <Card className={`pt-6`}>
      <CardContent className={`flex flex-col gap-4`}>
        <CardLayoutBox className="items-end">
          <CardBox variant="left">
            <div className="flex items-center gap-2">
              <h3>
                {t(
                  `opex.setup.category_accordion_item.detailed.second_level_category`
                )}
              </h3>
            </div>
            <div className="mt-2">
              <Input
                type="text"
                className={`mt-0 h-10`}
                value={channel.channel_name}
                readOnly
                tabIndex={-1}
                onChange={() => {}}
              />
            </div>
          </CardBox>
          <CardBox variant="right">
            <div className="flex items-center gap-2">
              <h3>
                {t(
                  `opex.setup.category_accordion_item.modelling_approach.label`
                )}
              </h3>
            </div>
            <div className="mt-2">
              <Select
                onValueChange={() => {}}
                value={
                  channel.sub_channels.length > 0
                    ? ``
                    : (channel.modeling_approach ?? ``)
                }
                disabled={channel.sub_channels.length >= 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Granularity" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={OPEX_MODELLING_APPROACH.ABSOLUTE_VALUE}>
                    {t(
                      `opex.setup.category_accordion_item.modelling_approach.absolute`
                    )}
                  </SelectItem>
                  <SelectItem
                    value={OPEX_MODELLING_APPROACH.PERCENTAGE_OF_REVENUE}
                  >
                    {t(
                      `opex.setup.category_accordion_item.modelling_approach.percent`
                    )}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardBox>
        </CardLayoutBox>
        {children}
      </CardContent>
    </Card>
  )
}

const DetailedSubChannelsTable = ({
  financialModelId,
  selectedCategoryId,
  channelId,
}: PropsWithChildren<{
  financialModelId: string
  selectedCategoryId: string
  channelId: string
}>) => {
  const { t } = useTranslation(`opex`)
  const { data: selectedCategory } = useOpexSelectedCategory({
    financialModelId,
    selectedCategoryId,
  })
  const { channels } = selectedCategory.opex_category

  const subChannels = channels.find((c) => c.id === channelId)?.sub_channels

  if (!subChannels) {
    throw new Error(
      `DetailedSubChannelsTable > No SubChannels for ${channelId} found`
    )
  }

  return (
    <TooltipProvider>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {t(
                `opex.setup.category_accordion_item.detailed.table_head.third_level_category`
              )}
            </TableHead>
            <TableHead>
              <span className="inline-flex items-center gap-2">
                <TableHeaderIcons icon="options" />
                {t(
                  `opex.setup.category_accordion_item.detailed.table_head.modelling_approach`
                )}
              </span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {subChannels.map((subChannel) => (
            <TableRow key={subChannel.id}>
              <TableCell className="w-1/2">
                <RowHeader type="output">
                  {subChannel.sub_channel_name ?? ``}
                </RowHeader>
              </TableCell>
              <OptionalTooltip
                show
                content={t(`opex.setup.tooltip.locked_categories`)}
              >
                <TableCell readonly className="w-1/2">
                  <TableCellContent className="max-w-none w-full justify-start px-2">
                    <span className="w-full h-full flex items-center justify-between">
                      <span className="text-disabled">-</span>
                      <Lock size={16} className="text-disabled" />
                    </span>
                  </TableCellContent>
                </TableCell>
              </OptionalTooltip>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TooltipProvider>
  )
}
const DEFAULT_SELECTED: string[] = []
export const SelectedCategoryContent = (
  selectedCategoryProps: SelectedCategoryProps
) => {
  const { data: selectedCategory } = useOpexSelectedCategory(
    selectedCategoryProps
  )
  const hasChildren = selectedCategory.opex_category.children.length > 0
  const hasSubChannels = selectedCategory.opex_category.channels.length > 0
  const isChildAndChannelsSectionVisible = hasChildren || hasSubChannels
  return (
    <div className={`flex flex-col gap-8`}>
      <TopLevelSelectedCategorySettings {...selectedCategoryProps} />
      {selectedCategory.granularity_level ===
        OPEX_GRANULARITY_LEVELS.DETAILED_EXPENSES && (
        <div className={`bg-gray-10 flex flex-col gap-8 p-6 rounded-lg`}>
          {isChildAndChannelsSectionVisible && (
            <div className={`flex flex-col gap-6`}>
              {selectedCategory.opex_category.channels.map((channel) => (
                <SelectedCategoryChannel
                  key={channel.id}
                  {...selectedCategoryProps}
                  channelId={channel.id}
                >
                  {channel.sub_channels.length > 0 && (
                    <DetailedSubChannelsTable
                      {...selectedCategoryProps}
                      channelId={channel.id}
                    />
                  )}
                </SelectedCategoryChannel>
              ))}
              {selectedCategory.opex_category.children.map((subCategory) => (
                <SecondLevelSubCategory
                  key={subCategory.id}
                  subCategoryId={subCategory.id}
                  {...selectedCategoryProps}
                >
                  {subCategory.children.length === 0 && (
                    <AddDetailedSubCostCategoryButton
                      {...selectedCategoryProps}
                      subCategoryId={subCategory.id}
                    />
                  )}
                  {subCategory.children.length > 0 && (
                    <TableActionPopoverProvider
                      defaultSelected={DEFAULT_SELECTED}
                    >
                      <DetailedSubCostCategoriesTable
                        {...selectedCategoryProps}
                        subCategoryId={subCategory.id}
                      />
                    </TableActionPopoverProvider>
                  )}
                </SecondLevelSubCategory>
              ))}
            </div>
          )}
          <AddSubCostCategoryButton {...selectedCategoryProps} />
        </div>
      )}
    </div>
  )
}
